import xAxios from "axios";
import { handleLogoutFn } from "common/helpers";
import { LOG_MODULES, trackException } from "common/errorTracker";
import Base from "common/constants";
import querystring from "querystring";

const isPrintMode = (typeof process !== 'undefined' && process.env && process.env.PRINTING_SERVER_MODE === 'true');

// Print server uses apiv3i (public endpoints, no auth cookies needed).
// Main server uses apiv4i via CF Worker (httpOnly cookie auth).
const axiosConfig = isPrintMode
  ? { baseURL: Base.APP_URLS.PRINT_API_BASE }
  : { baseURL: Base.APP_URLS.API_BASE, withCredentials: true };

const aiServer = {
  baseURL: Base.APP_URLS.AI_SERVER,
  withCredentials: true, // sends LG_ACCESS cookie to nodai/chat (Domain=.listgen.com in prod)
};

// Env override takes precedence over both modes
const envApiBase = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_BASE_URL)
  ? process.env.REACT_APP_API_BASE_URL
  : '';
if (envApiBase) {
  axiosConfig.baseURL = envApiBase;
}

const axios = xAxios.create(axiosConfig);
const aiServerConfig = xAxios.create(aiServer);

// Strip stale Bearer + Access-Control-Allow-Origin request headers set by legacy
// service files. CF Worker injects Authorization from httpOnly cookie instead.
// Basic auth headers (for /oauth/token login) are left untouched.
// Not applied in print mode — endpoints are public, no auth headers needed.
const stripLegacyAuthHeaders = (config) => {
  const auth = config.headers?.Authorization || config.headers?.authorization || '';
  if (typeof auth === 'string' && auth.startsWith('Bearer')) {
    delete config.headers.Authorization;
    delete config.headers.authorization;
  }
  // Access-Control-Allow-Origin is a response header — never valid on requests
  delete config.headers['Access-Control-Allow-Origin'];
  delete config.headers['access-control-allow-origin'];
  return config;
};

if (!isPrintMode) {
  axios.interceptors.request.use(stripLegacyAuthHeaders);
}
aiServerConfig.interceptors.request.use(stripLegacyAuthHeaders);

// Pull only small, known-safe fields out of a response body for logging
// (never the full payload, which may contain PII).
const extractApiMessage = (data) => {
  if (!data || typeof data !== 'object') {
    return undefined;
  }
  return (
    data.error_description ||
    data.errorMessage ||
    data.message ||
    (typeof data.error === 'string' ? data.error : undefined)
  );
};

const MAX_BODY_BYTES = 10_000;

// Safely extract and truncate a request/response body for Sentry.
// - Parses JSON strings so Sentry shows structured data, not a raw string.
// - Skips FormData / binary (not serializable).
// - Caps at MAX_BODY_BYTES to keep events readable.
const extractBody = (body) => {
  try {
    if (!body) return undefined;
    if (typeof body === 'string') {
      // axios serializes JSON payloads as strings — parse them back
      try { body = JSON.parse(body); } catch { /* not JSON — fall through */ }
    }
    if (typeof body === 'object' && typeof body.append === 'function') {
      return '[FormData]';
    }
    const serialized = typeof body === 'string' ? body : JSON.stringify(body);
    if (serialized.length > MAX_BODY_BYTES) {
      return serialized.slice(0, MAX_BODY_BYTES) + ` … [TRUNCATED at ${MAX_BODY_BYTES} chars]`;
    }
    return typeof body === 'string' ? body : body;
  } catch {
    return undefined;
  }
};

// ─── Route classification for 401 interceptor ────────────────────────────────
//
// AUTH_SCREEN_PATHS — login/registration screens that are intentionally
// unauthenticated. A 401 here means a stale in-flight request from the
// previous page arrived after the user was already redirected to login.
// Acting on it (silent refresh → logout) would cause a visible flash/reload
// on the login screen itself. Add a path here when:
//   • The route renders a login, registration, or password flow
//   • The page never makes authenticated API calls of its own
//
const AUTH_SCREEN_PATHS = [
  '/login',
  '/password/forgot',
  '/password/reset',
  '/register',
  '/agent/register',   // semi-auth register flow — not a full cookie session
];

// PUBLIC_PATHS — routes that are intentionally open (no cookie auth required).
// Their API calls go to apiv3i (public endpoints) or use token-based semi-auth,
// so a 401 here should never trigger a silent refresh or logout.
// Add a path here when:
//   • The route is accessible without a ListGen account (landing pages, print views)
//   • The route uses semi-auth (LP ROI, schedule) or no auth at all (print PMA)
//   • A 401 on this page does NOT mean the user's session expired
//
const PUBLIC_PATHS = [
  '/printpma',              // print PMA — no auth, uses apiv3i
  '/pdfprintversion',       // print PMA alias — no auth, uses apiv3i
  '/newpma',                // new PMA print variant — no auth, uses apiv3i
  '/agent/printroi',        // ROI print view — no auth, uses apiv3i
  '/lp/open',               // LP ROI / areas landing page — LP ROI semi-auth
  '/lp/schedule',           // schedule presentation landing page — semi-auth
  '/agent/open/website',    // agent public website — no auth
  '/validateAgentToken',    // token validator — sets semi-auth, no cookie session
  '/schedule-demo',         // public demo scheduling — no auth
];
// ─────────────────────────────────────────────────────────────────────────────

const isOnAuthScreen = () => {
  try {
    const pathname = window.location.pathname || '';
    return AUTH_SCREEN_PATHS.some((path) => pathname.startsWith(path));
  } catch {
    return false;
  }
};

const isOnPublicPath = () => {
  try {
    const pathname = window.location.pathname || '';
    return PUBLIC_PATHS.some((path) => pathname.startsWith(path));
  } catch {
    return false;
  }
};

// 401 triggers a forced logout (mode 6) — log which API caused it so we can
// trace the "forcefully asking for logout" complaints.
const log401ForcedLogout = (error, source) => {
  try {
    const response = error?.response;
    const config = error?.config || response?.config || {};
    const method = (config.method || 'get').toUpperCase();
    const url = String(config.url || '').split('?')[0];
    trackException({
      module: LOG_MODULES.API,
      code: 'HTTP_401_FORCED_LOGOUT',
      error: new Error(`${method} ${url} returned 401 — forcing logout`),
      httpStatus: 401,
      level: 'warning',
      data: {
        source,
        method,
        url,
        baseURL: config.baseURL,
        responseMessage: extractApiMessage(response?.data),
      },
    });
  } catch {
    // Logging must never break the request flow
  }
};

const logApiError = (error, source) => {
  try {
    const response = error?.response;
    const config = error?.config || response?.config || {};
    const status = response?.status;
    // 401 is logged separately via log401ForcedLogout
    if (status === 401) {
      return;
    }
    const method = (config.method || 'get').toUpperCase();
    const url = String(config.url || '').split('?')[0];
    trackException({
      module: LOG_MODULES.API,
      code: status ? `HTTP_${status}` : 'NETWORK_ERROR',
      error: new Error(
        `${method} ${url} failed (${status ?? error?.message ?? 'no response'})`
      ),
      httpStatus: status,
      data: {
        source,
        method,
        url,
        baseURL: config.baseURL,
        responseMessage: extractApiMessage(response?.data),
        requestBody: extractBody(config.data),
        responseBody: extractBody(response?.data),
      },
    });
  } catch {
    // Logging must never break the request flow
  }
};

const logApiSuccessFalse = (response, source) => {
  try {
    const data = response?.data;
    if (!data || typeof data !== 'object' || data.success !== false) {
      return;
    }
    const config = response.config || {};
    const method = (config.method || 'get').toUpperCase();
    const url = String(config.url || '').split('?')[0];
    trackException({
      module: LOG_MODULES.API,
      code: 'SUCCESS_FALSE',
      error: new Error(`${method} ${url} returned success=false`),
      httpStatus: response.status,
      level: 'warning',
      data: {
        source,
        method,
        url,
        baseURL: config.baseURL,
        responseMessage: extractApiMessage(data),
        requestBody: extractBody(config.data),
        responseBody: extractBody(data),
      },
    });
  } catch {
    // Logging must never break the request flow
  }
};

// One silent refresh attempt per app session — prevents refresh storms when
// multiple in-flight requests all 401 at once (e.g. browser reopen with
// expired LG_ACCESS but valid LG_REFRESH).
let silentRefreshPromise = null;

const attemptSilentRefresh = () => {
  if (silentRefreshPromise) return silentRefreshPromise;
  silentRefreshPromise = xAxios.post(
    Base.APP_URLS.API_BASE + '/oauth/token',
    querystring.stringify({ grant_type: 'refresh_token' }),
    {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      withCredentials: true,
    }
  ).finally(() => {
    // Reset after attempt so a genuine later expiry can retry
    silentRefreshPromise = null;
  });
  return silentRefreshPromise;
};

/**Intercept the axios */
axios.interceptors.response.use(
  (response) => {
    logApiSuccessFalse(response, 'api');
    return response;
  },
  async (error) => {
    logApiError(error, 'api');
    const status = error.response?.status;
    const config = error.config;
    // Skip refresh for the token endpoints themselves to avoid loops
    const isTokenEndpoint = config?.url?.includes('/oauth/token');
    const skipAuth = isOnAuthScreen() || isOnPublicPath();
    if (status === 401 && !skipAuth && !isTokenEndpoint && !config?._retried) {
      try {
        await attemptSilentRefresh();
        config._retried = true;
        return axios(config);
      } catch {
        log401ForcedLogout(error, 'api');
        handleLogoutFn(6);
      }
    } else if (status === 401 && !skipAuth) {
      log401ForcedLogout(error, 'api');
      handleLogoutFn(6);
    }
    return error;
  }
);

/**Intercept the webhook axios */
aiServerConfig.interceptors.response.use(
  (response) => {
    logApiSuccessFalse(response, 'ai_server');
    return response;
  },
  (error) => {
    logApiError(error, 'ai_server');
    if (error.response?.status === 401 && !isOnAuthScreen()) {
      log401ForcedLogout(error, 'ai_server');
      handleLogoutFn(6);
    }
    return error;
  }
);

/**
 * @name Default Axios
 * @description: This function is used to set default parameters for axios
 */
export default axios;

/**
 * @name AI Server Config
 * @description: This function is used for webhook calls to nodai.listgen.com
 */
export { aiServerConfig };